// Boilerplate Library admin — author the standard BSCR regulatory wording once,
// with {{tokens}} resolved from each building/client. Director/administrator edit; others view.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Plus, Trash2, Save, Loader2, ArrowLeft, BookText, Eye, Code2, EyeOff, FileText, AlignLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = new Set(["director", "administrator"]);
const inputCls = "w-full px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-[13px] focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]";

function resolve(body, tokens) {
    let out = body || "";
    for (const [k, v] of Object.entries(tokens || {})) out = out.replaceAll(`{{${k}}}`, v);
    return out;
}

export default function BoilerplateLibraryPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user && ADMIN_ROLES.has(user.role);
    const [data, setData] = useState(null);
    const [selKey, setSelKey] = useState(null);
    const [draft, setDraft] = useState(null);     // {title, body} being edited
    const [preview, setPreview] = useState(true);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);

    async function load(selectKey) {
        const { data } = await api.get("/bsc/boilerplate/templates");
        setData(data);
        const k = selectKey || selKey || data.templates[0]?.key;
        setSelKey(k);
        const t = data.templates.find((x) => x.key === k);
        if (t) setDraft({ title: t.title, body: t.body });
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const tpl = useMemo(() => data?.templates.find((t) => t.key === selKey), [data, selKey]);
    const tokens = data?.sample_tokens || {};

    function select(t) {
        setSelKey(t.key); setDraft({ title: t.title, body: t.body }); setCreating(false);
    }

    function insertToken(tok) {
        setDraft((d) => ({ ...d, body: (d.body || "") + `{{${tok}}}` }));
    }

    async function save() {
        setBusy(true);
        try { await api.put(`/bsc/boilerplate/templates/${selKey}`, { title: draft.title, body: draft.body }); toast.success("Template saved"); await load(selKey); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(false); }
    }
    async function toggleActive(t) {
        try { await api.put(`/bsc/boilerplate/templates/${t.key}`, { active: !t.active }); await load(selKey); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function remove(t) {
        if (!window.confirm(t.custom ? `Delete custom template "${t.title}"?` : `Deactivate standard template "${t.title}"? (It can be re-enabled later.)`)) return;
        try { await api.delete(`/bsc/boilerplate/templates/${t.key}`); toast.success(t.custom ? "Deleted" : "Deactivated"); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }

    if (!data) return <div className="p-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#9A968F]" /></div>;

    const sectionTitle = (code) => data.targets.find((x) => x.section_code === code)?.title || code;

    return (
        <div data-testid="boilerplate-library-page" className="space-y-5 pb-10">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <button onClick={() => navigate("/app/bsc")} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#8A5A1E] mb-1"><ArrowLeft size={13} /> Back to Building Safety Cases</button>
                    <h1 className="text-[22px] font-bold text-[#1F1F1F] flex items-center gap-2"><BookText size={20} className="text-[var(--brand-accent,#C75A1B)]" /> Boilerplate Library</h1>
                    <p className="text-[12.5px] text-[#6F6A64]">Standard regulatory wording with tokens resolved per building. Refine once — every report inherits it.</p>
                </div>
                {isAdmin && (
                    <button data-testid="bp-new-template" onClick={() => { setCreating(true); setSelKey(null); }}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        <Plus size={14} /> New template
                    </button>
                )}
            </div>

            <div className="grid grid-cols-12 gap-5 items-start">
                {/* List */}
                <div className="col-span-12 lg:col-span-4 rounded-2xl border border-[#E5E0DA] bg-white p-2 max-h-[78vh] overflow-y-auto">
                    {data.templates.map((t) => (
                        <button key={t.key} data-testid={`bp-item-${t.key}`} onClick={() => select(t)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg transition flex items-start gap-2.5 ${selKey === t.key && !creating ? "bg-[#FBEFE5]" : "hover:bg-[#F6F4F0]"} ${!t.active ? "opacity-55" : ""}`}>
                            <span className="mt-0.5 text-[var(--brand-accent,#C75A1B)]">{t.field_key ? <FileText size={14} /> : <AlignLeft size={14} />}</span>
                            <span className="flex-1 min-w-0">
                                <span className="block text-[13px] font-semibold text-[#1F1F1F] truncate">{t.title}</span>
                                <span className="block text-[11px] text-[#9A968F] truncate">{sectionTitle(t.section_code)} · {t.field_key ? "field" : "narrative"}{t.custom ? " · custom" : ""}{!t.active ? " · inactive" : ""}</span>
                            </span>
                        </button>
                    ))}
                </div>

                {/* Editor / Create */}
                <div className="col-span-12 lg:col-span-8 rounded-2xl border border-[#E5E0DA] bg-white p-5">
                    {creating ? (
                        <CreateForm targets={data.targets} tokens={tokens} onCancel={() => { setCreating(false); load(); }}
                            onCreated={(t) => { setCreating(false); load(t.key); }} />
                    ) : tpl && draft ? (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <span className="text-[10.5px] uppercase tracking-wide font-semibold text-[#9A968F]">{sectionTitle(tpl.section_code)} · {tpl.field_key ? `field (${tpl.field_key})` : "section narrative"}</span>
                                    <input data-testid="bp-edit-title" disabled={!isAdmin} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                                        className="block w-full text-[16px] font-bold text-[#1F1F1F] border-0 border-b border-transparent focus:border-[var(--brand-accent,#C75A1B)] outline-none mt-0.5 bg-transparent" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setPreview((v) => !v)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] text-[11.5px] font-semibold text-[#6F6A64]">
                                        {preview ? <Code2 size={13} /> : <Eye size={13} />} {preview ? "Show tokens" : "Live preview"}
                                    </button>
                                    {isAdmin && <button data-testid="bp-toggle-active" onClick={() => toggleActive(tpl)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] text-[11.5px] font-semibold text-[#6F6A64]">{tpl.active ? <><EyeOff size={13} /> Deactivate</> : <><Eye size={13} /> Activate</>}</button>}
                                    {isAdmin && <button data-testid="bp-delete" onClick={() => remove(tpl)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#F0D9D5] text-[11.5px] font-semibold text-[#B33A2E]"><Trash2 size={13} /> {tpl.custom ? "Delete" : "Deactivate"}</button>}
                                </div>
                            </div>

                            {isAdmin && (
                                <div className="flex flex-wrap gap-1.5">
                                    {data.tokens.map((tok) => (
                                        <button key={tok} data-testid={`bp-token-${tok}`} onClick={() => insertToken(tok)}
                                            className="px-2 py-1 rounded-md bg-[#EEF3FB] text-[#1F6FE5] text-[11px] font-mono font-semibold hover:bg-[#E0ECFB]" title={`Resolves to: ${tokens[tok]}`}>{`{{${tok}}}`}</button>
                                    ))}
                                </div>
                            )}

                            <div className="grid gap-4">
                                <div>
                                    <label className="text-[11px] font-semibold text-[#6F6A64] mb-1 block">Template text</label>
                                    <textarea data-testid="bp-edit-body" disabled={!isAdmin} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                                        rows={12} className={`${inputCls} font-mono leading-relaxed`} />
                                </div>
                                {preview && (
                                    <div data-testid="bp-preview" className="rounded-xl border border-[#EFEAE4] bg-[#FCFBF9] p-4">
                                        <span className="text-[10.5px] uppercase tracking-wide font-semibold text-[#9A968F] flex items-center gap-1.5 mb-2"><Eye size={12} /> Live preview (example values)</span>
                                        <p className="text-[12.5px] text-[#2b2b2b] whitespace-pre-wrap leading-relaxed">{resolve(draft.body, tokens)}</p>
                                    </div>
                                )}
                            </div>

                            {isAdmin && (
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] text-[#9A968F]">{tpl.updated_by ? `Last edited by ${tpl.updated_by}` : ""}</span>
                                    <button data-testid="bp-save" onClick={save} disabled={busy}
                                        className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save template
                                    </button>
                                </div>
                            )}
                            {!isAdmin && <p className="text-[12px] italic text-[#9A968F]">View only — director or administrator role required to edit the library.</p>}
                        </div>
                    ) : <p className="text-[13px] text-[#9A968F]">Select a template to view or edit.</p>}
                </div>
            </div>
        </div>
    );
}

function CreateForm({ targets, tokens, onCancel, onCreated }) {
    const [title, setTitle] = useState("");
    const [sectionCode, setSectionCode] = useState(targets[0]?.section_code || "");
    const [fieldKey, setFieldKey] = useState("");   // "" => narrative
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);
    const section = targets.find((t) => t.section_code === sectionCode);

    async function create() {
        if (!title.trim()) { toast.error("Give the template a title"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/bsc/boilerplate/templates", {
                title, section_code: sectionCode, field_key: fieldKey || null, body,
            });
            toast.success("Template created"); onCreated(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Create failed"); }
        finally { setBusy(false); }
    }

    return (
        <div data-testid="bp-create-form" className="space-y-4">
            <h3 className="text-[16px] font-bold text-[#1F1F1F]">New boilerplate template</h3>
            <div>
                <label className="text-[11px] font-semibold text-[#6F6A64] mb-1 block">Title</label>
                <input data-testid="bp-create-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="e.g. Accessibility statement" />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-[11px] font-semibold text-[#6F6A64] mb-1 block">Section</label>
                    <select data-testid="bp-create-section" value={sectionCode} onChange={(e) => { setSectionCode(e.target.value); setFieldKey(""); }} className={inputCls}>
                        {targets.map((t) => <option key={t.section_code} value={t.section_code}>{t.title}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[11px] font-semibold text-[#6F6A64] mb-1 block">Fills</label>
                    <select data-testid="bp-create-field" value={fieldKey} onChange={(e) => setFieldKey(e.target.value)} className={inputCls}>
                        <option value="">Section narrative</option>
                        {(section?.fields || []).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                </div>
            </div>
            <div>
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {Object.keys(tokens).map((tok) => (
                        <button key={tok} onClick={() => setBody((b) => b + `{{${tok}}}`)} className="px-2 py-1 rounded-md bg-[#EEF3FB] text-[#1F6FE5] text-[11px] font-mono font-semibold hover:bg-[#E0ECFB]">{`{{${tok}}}`}</button>
                    ))}
                </div>
                <textarea data-testid="bp-create-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className={`${inputCls} font-mono`} placeholder="Standard wording. Use {{TOKENS}} that will resolve per building." />
            </div>
            {body && (
                <div className="rounded-xl border border-[#EFEAE4] bg-[#FCFBF9] p-3">
                    <span className="text-[10.5px] uppercase tracking-wide font-semibold text-[#9A968F]">Preview</span>
                    <p className="text-[12.5px] text-[#2b2b2b] whitespace-pre-wrap mt-1">{resolve(body, tokens)}</p>
                </div>
            )}
            <div className="flex justify-end gap-2">
                <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-[#E5E0DA] text-[12.5px] font-semibold text-[#6F6A64]">Cancel</button>
                <button data-testid="bp-create-submit" onClick={create} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create template</button>
            </div>
        </div>
    );
}
